"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useProjects } from "@/hooks/use-projects";
import { supabase } from "@/lib/supabase";
import { FolderPlusIcon, LockIcon, UsersIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

/**
 * Two-step "Move to project" flow: pick a project, then pick who can see the
 * chat there. Shared by the open-chat header menu (ChatHeader) and the chats
 * list page — both physically migrate a personal chat via
 * POST /api/chats/[chatId]/move-to-project.
 */
export const MoveChatToProjectDialog = ({
  chatId,
  userId,
  open,
  onOpenChange,
  onMoved,
}: {
  chatId: string;
  userId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the chat is successfully moved. */
  onMoved?: () => void;
}) => {
  const { projects } = useProjects();
  const [pendingProjectId, setPendingProjectId] = useState<string | null>(null);
  const [canSharePending, setCanSharePending] = useState(false);
  const [shareWithProject, setShareWithProject] = useState(false);
  const [isMoving, setIsMoving] = useState(false);

  // When user picks a project, check if they're owner or a member (only
  // those can choose to share the chat with the rest of the project).
  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!pendingProjectId) { if (!cancelled) setCanSharePending(false); return; }
      const project = projects.find((p) => p.id === pendingProjectId);
      if (project && userId && project.owner_id === userId) {
        if (!cancelled) setCanSharePending(true);
        return;
      }
      const { data } = await supabase
        .from("project_members")
        .select("role")
        .eq("project_id", pendingProjectId)
        .eq("user_id", userId ?? "")
        .maybeSingle();
      if (!cancelled) setCanSharePending(!!data);
    }
    check();
    return () => { cancelled = true; };
  }, [pendingProjectId, userId, projects]);

  const reset = useCallback(() => {
    setPendingProjectId(null);
    setShareWithProject(false);
  }, []);

  const handleMove = useCallback(async (targetProjectId: string) => {
    setIsMoving(true);
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const res = await fetch(`/api/chats/${chatId}/move-to-project`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ projectId: targetProjectId, shared: shareWithProject }),
    });
    setIsMoving(false);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      toast.error((body as { error?: string }).error ?? "Failed to move chat to project.");
      return;
    }
    toast.success(shareWithProject ? "Chat moved and shared with project." : "Chat moved to project.");
    reset();
    onOpenChange(false);
    onMoved?.();
  }, [chatId, shareWithProject, reset, onOpenChange, onMoved]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        {pendingProjectId === null ? (
          <>
            <DialogHeader>
              <DialogTitle>Move to project</DialogTitle>
              <DialogDescription>
                Choose a project. The chat will be moved and removed from your personal history.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              {projects.length === 0 ? (
                <p className="text-sm text-muted-foreground">No projects yet. Create a project first.</p>
              ) : (
                projects.map((project) => (
                  <Button
                    key={project.id}
                    variant="outline"
                    className="justify-start"
                    onClick={() => { setPendingProjectId(project.id); setShareWithProject(false); }}
                    type="button"
                  >
                    <FolderPlusIcon className="mr-2 size-4" />
                    {project.name}
                  </Button>
                ))
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} type="button">
                Cancel
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                Move to &ldquo;{projects.find((p) => p.id === pendingProjectId)?.name}&rdquo;
              </DialogTitle>
              <DialogDescription>
                Who should be able to see this chat?
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setShareWithProject(false)}
                className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${!shareWithProject ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}
              >
                <LockIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Private</p>
                  <p className="text-xs text-muted-foreground">Only visible to you.</p>
                </div>
              </button>
              {canSharePending && (
                <button
                  type="button"
                  onClick={() => setShareWithProject(true)}
                  className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${shareWithProject ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}
                >
                  <UsersIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Shared with project</p>
                    <p className="text-xs text-muted-foreground">All members can read. Only editors can reply.</p>
                  </div>
                </button>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setPendingProjectId(null); setShareWithProject(false); }}
                disabled={isMoving}
                type="button"
              >
                Back
              </Button>
              <Button
                onClick={() => void handleMove(pendingProjectId)}
                disabled={isMoving}
                type="button"
              >
                {isMoving ? "Moving…" : "Move to project"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
